import { Suspense, useCallback, useEffect, useMemo } from 'react';
import {
    Button,
    Card,
    Cell,
    HeaderCell,
    HStack,
    IconLink,
    MiniGrid,
    MiniGridHeaderRow,
    MiniGridRow,
    Select,
    Stub,
    StubCell,
    Text,
    TextCell,
} from '@direct-frontend/components';
import { useGetDeliveryWarehouses } from '#entities/warehousePoint';
import { type OpenApiSchemaDeliveryWarehouse } from '#shared/api/schema';
import { ErrorBlankSad } from '#shared/components/ErrorBlank';
import { OZON_SERVICE_NAME } from '#shared/lib/brand';
import {
    getMainWarehouseCaption,
    getMainWarehouseIcon,
    type SupportedIntegrationTypes,
} from '#shared/lib/integrations';
import { IconOzonLogistics } from '#shared/ui/icons';
import { type WarehouseToCompare } from '../../types';
import styles from './CompareWarehousesMiniGrid.module.css';

export const CompareWarehousesMiniGrid = ({
    warehousesToCompare,
    setWarehousesToCompare,
    resetErrorMessage,
    integrationType,
}: {
    warehousesToCompare: WarehouseToCompare[];
    setWarehousesToCompare: React.Dispatch<React.SetStateAction<WarehouseToCompare[]>>;
    resetErrorMessage: VoidFunction;
    integrationType?: SupportedIntegrationTypes;
}) => {
    const mainWarehouseCaption = getMainWarehouseCaption(integrationType);
    const mainWarehouseIcon = getMainWarehouseIcon(integrationType);

    return (
        <MiniGrid>
            <MiniGridHeaderRow>
                <HeaderCell
                    className={styles.cell}
                    title={
                        <HStack gap="12">
                            <IconOzonLogistics size="20" />
                            <Text role="supplementary" size="s">
                                Склад {OZON_SERVICE_NAME} FBS
                            </Text>
                        </HStack>
                    }
                />
                <Cell type="Icon" isGrowable={false}>
                    {mainWarehouseIcon}
                </Cell>
                <HeaderCell className={styles.cell} title={mainWarehouseCaption} />
            </MiniGridHeaderRow>
            <Suspense fallback={<CompareWarehousesMiniGridStub />}>
                <CompareWarehousesMiniGridContent
                    warehousesToCompare={warehousesToCompare}
                    setWarehousesToCompare={setWarehousesToCompare}
                    resetErrorMessage={resetErrorMessage}
                />
            </Suspense>
        </MiniGrid>
    );
};

export const CompareWarehousesMiniGridContent = ({
    warehousesToCompare,
    setWarehousesToCompare,
    resetErrorMessage,
}: {
    warehousesToCompare: WarehouseToCompare[];
    setWarehousesToCompare: React.Dispatch<React.SetStateAction<WarehouseToCompare[]>>;
    resetErrorMessage: VoidFunction;
}) => {
    const {
        data: generalWarehouses = [],
        isFetching: isFetchingGeneralWarehouses,
        isError: isErrorGeneralWarehouses,
        refetch: refetchGeneralWarehouses,
    } = useGetDeliveryWarehouses({
        searchParams: { type: 'GENERAL' },
    });

    const {
        data: ozonWarehouses = [],
        isFetching: isFetchingOzonWarehouses,
        isError: isErrorOzonWarehouses,
        refetch: refetchOzonWarehouses,
    } = useGetDeliveryWarehouses({
        searchParams: { type: 'OZON_FBS' },
    });

    useEffect(() => {
        refetchGeneralWarehouses();
        refetchOzonWarehouses();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- отключаем кэширование здесь и рефетчим склады при перерендерах
    }, []);

    useEffect(() => {
        setWarehousesToCompare(
            ozonWarehouses.map((warehouse) => {
                return {
                    id: warehouse.id,
                    title: warehouse.title,
                    twin_warehouse_id: warehouse.twin_warehouse_id,
                };
            })
        );
    }, [ozonWarehouses, setWarehousesToCompare]);

    const handleWarehouseSelect = useCallback(
        (warehouseId: string, selectedValue: string) => {
            setWarehousesToCompare((prevWarehouses) =>
                prevWarehouses.map((warehouse) => {
                    if (warehouse.id === warehouseId) {
                        return {
                            ...warehouse,
                            twin_warehouse_id: selectedValue,
                        };
                    }

                    return warehouse;
                })
            );

            resetErrorMessage();
        },
        [setWarehousesToCompare, resetErrorMessage]
    );

    if (isFetchingGeneralWarehouses || isFetchingOzonWarehouses) {
        return <CompareWarehousesMiniGridStub />;
    }

    if (isErrorGeneralWarehouses || isErrorOzonWarehouses) {
        return (
            <CompareWarehousesMiniGridError
                onRefetch={() => {
                    refetchGeneralWarehouses();
                    refetchOzonWarehouses();
                }}
            />
        );
    }

    if (ozonWarehouses.length === 0 || generalWarehouses.length === 0) {
        return (
            <CompareWarehousesMiniGridEmpty
                isOzonWarehousesEmpty={ozonWarehouses.length === 0}
                isGeneralWarehousesEmpty={generalWarehouses.length === 0}
            />
        );
    }

    return (
        <>
            {[...ozonWarehouses].map((ozonWarehouse) => (
                <CompareWarehousesMiniGridRow
                    key={ozonWarehouse.id}
                    ozonWarehouse={ozonWarehouse}
                    generalWarehouses={generalWarehouses}
                    warehousesToCompare={warehousesToCompare}
                    handleWarehouseSelect={handleWarehouseSelect}
                />
            ))}
        </>
    );
};

const CompareWarehousesMiniGridRow = ({
    ozonWarehouse,
    generalWarehouses,
    warehousesToCompare,
    handleWarehouseSelect,
}: {
    ozonWarehouse: OpenApiSchemaDeliveryWarehouse;
    generalWarehouses: OpenApiSchemaDeliveryWarehouse[];
    warehousesToCompare: WarehouseToCompare[];
    handleWarehouseSelect: (warehouseId: string, selectedValue: string) => void;
}) => {
    const items = useMemo(
        () =>
            generalWarehouses.map((generalWarehouse) => {
                return {
                    content: generalWarehouse.title,
                    value: generalWarehouse.id,
                };
            }),
        [generalWarehouses]
    );

    const value = useMemo(
        () => warehousesToCompare.find((warehouse) => warehouse.id === ozonWarehouse.id)?.twin_warehouse_id ?? '',
        [warehousesToCompare, ozonWarehouse.id]
    );

    return (
        <MiniGridRow
            key={`CompareWarehousesMiniGrid.Content.${ozonWarehouse.id}`}
            id={`CompareWarehousesMiniGrid.Content.${ozonWarehouse.id}`}
            data-testid={`CompareWarehousesMiniGrid.Content.${ozonWarehouse.id}`}
        >
            <TextCell
                className={styles.cell}
                title={ozonWarehouse.title}
                caption={ozonWarehouse.courier_info.courier_address ?? 'Адрес не указан'}
            />
            <Cell type="Icon" isGrowable={false}>
                <IconLink role="supplementary" color="gray" size="20" />
            </Cell>
            <Cell type="Select" className={styles.cell}>
                <Select
                    items={items}
                    size="xs"
                    width="max"
                    color="contour"
                    aria-label="Выберите склад"
                    placeholder="Выберите склад"
                    value={value}
                    onChange={(value: string) => {
                        handleWarehouseSelect(ozonWarehouse.id, value);
                    }}
                />
            </Cell>
        </MiniGridRow>
    );
};

export const CompareWarehousesMiniGridStub = () => (
    <>
        {Array.from({ length: 3 }).map((_, index) => (
            <MiniGridRow
                key={`CompareWarehousesMiniGrid.Stub.${index}`}
                id={`CompareWarehousesMiniGrid.Stub.${index}`}
                data-testid={`CompareWarehousesMiniGrid.Stub.${index}`}
            >
                <StubCell isGrowable withCaption className={styles.cell} />
                <Cell type="Icon" isGrowable={false}>
                    <Stub height={20} width={20} borderRadius="8" />
                </Cell>
                <StubCell isGrowable className={styles.cell} />
            </MiniGridRow>
        ))}
    </>
);

const CompareWarehousesMiniGridError = ({ onRefetch }: { onRefetch: VoidFunction }) => (
    <Card padding="16">
        <ErrorBlankSad
            title={`Произошла ошибка при загрузке складов ${OZON_SERVICE_NAME} FBS и основного склада`}
            controls={
                <Button size="m" color="normal" onClick={onRefetch}>
                    Попробовать еще раз
                </Button>
            }
        />
    </Card>
);

const CompareWarehousesMiniGridEmpty = ({
    isOzonWarehousesEmpty,
    isGeneralWarehousesEmpty,
}: {
    isOzonWarehousesEmpty: boolean;
    isGeneralWarehousesEmpty: boolean;
}) => {
    if (isOzonWarehousesEmpty) {
        return (
            <Card padding="16">
                <ErrorBlankSad
                    subtitle={`Необходимо завести хотя бы один склад в ${OZON_SERVICE_NAME}`}
                    title={`Не найдено ни одного склада для ${OZON_SERVICE_NAME} FBS`}
                />
            </Card>
        );
    }

    if (isGeneralWarehousesEmpty) {
        return (
            <Card padding="16">
                <ErrorBlankSad
                    subtitle="Сделайте синк остатков или создайте их во внешней системе"
                    title="Нет складов для сопоставления"
                />
            </Card>
        );
    }

    return null;
};
